import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TokenService } from './token-service';
import { RequestResult } from '../interfaces/request-result';
import Swal from 'sweetalert2';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import { throwError } from 'rxjs/internal/observable/throwError';
import { of } from 'rxjs/internal/observable/of';
@Injectable({
  providedIn: 'root',
})
export class BackendService {
  constructor(
    private httpClient: HttpClient,
    private tokenService: TokenService,
  ) {}

  private apiUrl: string = environment.apiUrl;

  private buildFullUrl(url: string): string {
    // 移除多餘的斜線避免網址錯誤，例如 /FormEngine//api/LoginUserData
    const baseUrl = this.apiUrl.endsWith('/') ? this.apiUrl : this.apiUrl + '/';
    const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
    return baseUrl + cleanUrl;
  }

  // 簡化後的 getHeaders，只負責基本的 Content-Type
  getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  addAuthorizationTokenToOptions(options?: { headers?: HttpHeaders }): { headers: HttpHeaders } {
    const tokenValue = `Bearer ${this.tokenService.getLoginToken()}`;
    let headers = options?.headers ?? new HttpHeaders();
    // debugger;
    if (!headers.has('Authorization')) {
      headers = headers.set('Authorization', tokenValue);
    }
    return { headers };
  }

  getAcceptBlobResponseOptions() {
    return { responseType: 'blob' as 'json' };
  }

  private handleResponse<T_Data>(response: RequestResult<T_Data>): T_Data | null {
    // 這裡根據報錯提示，將 isSuccess 改為 Success
    if (response && response.Success) {
      return response.Data || null;
    }
    // 當 Success = false 時，拋出錯誤讓 catchError 處理
    const errorMessage = response?.Message || '未知錯誤';
    throw new Error(errorMessage);
  }


  private handleError(error: HttpErrorResponse | Error): Observable<never> {
    let errorMessage = '未知錯誤';

    if (error instanceof HttpErrorResponse) {
      // HTTP 錯誤
      if (error.status === 401) {
        errorMessage = '您的登入資訊已過期，請重新登入';
        Swal.fire('驗證失敗', errorMessage, 'error');
      } else if (error.status === 403) {
        errorMessage = '您沒有權限執行此操作';
      } else if (error.status === 404) {
        errorMessage = '無法找到所請求的資源';
      } else if (error.status === 500) {
        errorMessage = '伺服器發生錯誤，請稍後再試';
      } else if (error.status === 0) {
        errorMessage = '無法連線到伺服器';
      } else {
        // 嘗試從響應體提取 Message
        if (error.error instanceof ErrorEvent) {
          // 用戶端錯誤
          errorMessage = error.error.message;
        } else {
          // 伺服器返回的錯誤響應
          if (error.error?.Message) {
            errorMessage = error.error.Message;
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (typeof error.error === 'string') {
            errorMessage = error.error;
          }
        }
      }
    } else if (error instanceof Error) {
      // JavaScript Error（包括我們拋出的 Error）
      errorMessage = error.message;
      Swal.fire('發生錯誤', errorMessage, 'error');
    }

    return throwError(() => new Error(errorMessage));
  }


  download(url: string): Observable<Blob> {
    const options = this.getAcceptBlobResponseOptions();
    return this.httpClient
      .get(this.buildFullUrl(url), { headers: this.getHeaders(), responseType: 'blob' as 'json' })
      .pipe(
        mergeMap((response) =>
          response instanceof Blob
            ? of(response)
            : throwError(() => new Error('Invalid Blob response')),
        ),
        catchError((err) => {
          this.handleError(err);
          return throwError(() => err);
        }),
      );
  }

  /**
   * Performs a request with `get` http method.
   */
  get<T_Data>(url: string, options?: { headers?: HttpHeaders }): Observable<T_Data | null> {
    return this.httpClient
      .get<
        RequestResult<T_Data>
      >(this.buildFullUrl(url), { headers: options?.headers || this.getHeaders() })
      .pipe(
        map((response) => this.handleResponse<T_Data>(response)),
        catchError((err) => this.handleError(err)),
      );
  }

  /**
   * Performs a request with `post` http method.
   */
  post<T_Data>(
    url: string,
    body: any,
    options?: { headers?: HttpHeaders },
  ): Observable<T_Data | null> {

    const headers = body instanceof FormData ? undefined : options?.headers || this.getHeaders();
    return this.httpClient.post<RequestResult<T_Data>>(this.buildFullUrl(url), body, { headers })
      .pipe(
        map((response) => this.handleResponse<T_Data>(response)),
        catchError((err) => this.handleError(err)),
      );

  }

  /**
   * Performs a request with `put` http method.
   */
  put<T_Data>(
    url: string,
    body: any,
    options?: { headers?: HttpHeaders },
  ): Observable<T_Data | null> {
    return this.httpClient
      .put<
        RequestResult<T_Data>
      >(this.buildFullUrl(url), body, this.addAuthorizationTokenToOptions(options))
      .pipe(
        filter(
          (response: RequestResult<T_Data> | null): response is RequestResult<T_Data> =>
            response !== null,
        ),
        map((response) => this.handleResponse<T_Data>(response)), // handleResponse 現在回傳 T_Data | null
        catchError((err) => {
          this.handleError(err);
          return throwError(() => err);
        }),
      );
  }

  /**
   * Performs a request with `delete` http method.
   */
  delete<T_Data>(url: string, options?: { headers?: HttpHeaders }): Observable<T_Data | null> {
    return this.httpClient
      .delete<
        RequestResult<T_Data>
      >(this.buildFullUrl(url), this.addAuthorizationTokenToOptions(options))
      .pipe(
        filter(
          (response: RequestResult<T_Data> | null): response is RequestResult<T_Data> =>
            response !== null,
        ),
        map((response) => this.handleResponse<T_Data>(response)), // handleResponse 現在回傳 T_Data | null
        catchError((err) => {
          this.handleError(err);
          return throwError(() => err);
        }),
      );
  }

  /**
   * Performs a request with `patch` http method.
   */
  patch<T_Data>(
    url: string,
    body: any,
    options?: { headers?: HttpHeaders },
  ): Observable<RequestResult<T_Data>> {
    return this.httpClient
      .patch<RequestResult<T_Data>>
      (this.buildFullUrl(url), body, this.addAuthorizationTokenToOptions(options))
      .pipe(
        filter(
          (response: RequestResult<T_Data> | null): response is RequestResult<T_Data> =>
            response !== null,
        ),        
        catchError((err) => {
          this.handleError(err);
          return throwError(() => err);
        }),
      );
  }

  /**
   * Performs a request with `head` http method.
   */
  head<T_Data>(url: string, options?: { headers?: HttpHeaders }): Observable<T_Data | null> {
    return this.httpClient
      .head<
        RequestResult<T_Data>
      >(this.buildFullUrl(url), this.addAuthorizationTokenToOptions(options))
      .pipe(
        filter(
          (response: RequestResult<T_Data> | null): response is RequestResult<T_Data> =>
            response !== null,
        ),
        map((response) => this.handleResponse<T_Data>(response)), // handleResponse 現在回傳 T_Data | null
        catchError((err) => {
          this.handleError(err);
          return throwError(() => err);
        }),
      );
  }

  /**
   * Performs a request with `options` http method.
   */
  options<T_Data>(url: string, options?: { headers?: HttpHeaders }): Observable<T_Data | null> {
    return this.httpClient
      .options<
        RequestResult<T_Data>
      >(this.buildFullUrl(url), this.addAuthorizationTokenToOptions(options))
      .pipe(
        filter(
          (response: RequestResult<T_Data> | null): response is RequestResult<T_Data> =>
            response !== null,
        ),
        map((response) => this.handleResponse<T_Data>(response)), // handleResponse 現在回傳 T_Data | null
        catchError((err) => {
          this.handleError(err);
          return throwError(() => err);
        }),
      );
  }
}
